// src/components/templates/diary/ToolsMarketplace.jsx
// Enhanced Marketplace with Workspace Selection & Owner Editing
import React, { useState, useMemo, useEffect } from 'react';
import {
  ArrowLeft, Search, Star, Download, Check, ChevronRight, ChevronDown,
  Target, Receipt, Calendar, AlertTriangle, PieChart, Package,
  Zap, BarChart3, FileText, Truck, Scale, Heart, X, ExternalLink,
  Clock, Users, Shield, Tag, Filter, Grid, List, Sparkles, TrendingUp,
  Building2, User, Crown, Edit3, Save, Loader2, CheckCircle, Info,
  Eye, Lock, Unlock, Settings2, BadgeCheck, Layers, CalendarRange
} from 'lucide-react';
import { toolsService, TOOL_CATALOG, ToolCategory, ToolPricing } from '../../services/toolsService';

// ============================================================
// CONSTANTS
// ============================================================

const IconMap = {
  Target, Receipt, Calendar, AlertTriangle, PieChart, Package,
  Zap, BarChart3, FileText, Truck, Scale, Heart, TrendingUp,
  Users, CalendarRange
};

const CategoryConfig = {
  [ToolCategory.PRODUCTIVITY]: { icon: Zap, label: 'Productivity', color: 'blue', gradient: 'from-blue-500 to-cyan-500' },
  [ToolCategory.FINANCE]: { icon: Receipt, label: 'Finance', color: 'emerald', gradient: 'from-emerald-500 to-teal-500' },
  [ToolCategory.EDUCATION]: { icon: Target, label: 'Education', color: 'purple', gradient: 'from-purple-500 to-indigo-500' },
  [ToolCategory.PLANNING]: { icon: Calendar, label: 'Planning', color: 'amber', gradient: 'from-amber-500 to-orange-500' },
  [ToolCategory.ANALYSIS]: { icon: BarChart3, label: 'Analysis', color: 'cyan', gradient: 'from-cyan-500 to-blue-500' },
  [ToolCategory.DOCUMENTS]: { icon: FileText, label: 'Documents', color: 'orange', gradient: 'from-orange-500 to-red-500' },
  [ToolCategory.LOGISTICS]: { icon: Truck, label: 'Logistics', color: 'indigo', gradient: 'from-indigo-500 to-purple-500' },
  [ToolCategory.LEGAL]: { icon: Scale, label: 'Legal', color: 'rose', gradient: 'from-rose-500 to-pink-500' },
  [ToolCategory.HEALTHCARE]: { icon: Heart, label: 'Healthcare', color: 'pink', gradient: 'from-pink-500 to-rose-500' },
  [ToolCategory.CUSTOM]: { icon: Package, label: 'Custom', color: 'slate', gradient: 'from-slate-500 to-gray-500' }
};

// ============================================================
// UI COMPONENTS
// ============================================================

const PricingBadge = ({ pricing, price, currency = 'ZAR', size = 'sm' }) => {
  const sizeClasses = size === 'lg' ? 'px-3 py-1.5 text-sm' : 'px-2 py-0.5 text-xs';
  
  if (pricing === ToolPricing.FREE) {
    return (
      <span className={`${sizeClasses} bg-gradient-to-r from-emerald-500 to-green-500 text-white font-semibold rounded-full shadow-sm`}>
        Free
      </span>
    );
  }
  if (pricing === ToolPricing.FREEMIUM) {
    return (
      <span className={`${sizeClasses} bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-full shadow-sm`}>
        Freemium
      </span>
    );
  }
  if (pricing === ToolPricing.PAID) {
    return (
      <span className={`${sizeClasses} bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-full shadow-sm`}>
        {currency} {price}
      </span>
    );
  }
  if (pricing === ToolPricing.SUBSCRIPTION) {
    return (
      <span className={`${sizeClasses} bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-full shadow-sm`}>
        {currency} {price}/mo
      </span>
    );
  }
  return null;
};

const CategoryPill = ({ category, selected, onClick, count }) => {
  const config = CategoryConfig[category];
  if (!config) return null;
  const Icon = config.icon;
  
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
        selected 
          ? `bg-gradient-to-r ${config.gradient} text-white shadow-lg shadow-${config.color}-500/25` 
          : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300 hover:shadow-sm'
      }`}
    >
      <Icon className="w-4 h-4" />
      {config.label}
      {count > 0 && (
        <span className={`px-1.5 py-0.5 rounded-full text-xs ${
          selected ? 'bg-white/20' : 'bg-gray-100'
        }`}>
          {count}
        </span>
      )}
    </button>
  );
};

const FeatureCheckItem = ({ children }) => (
  <div className="flex items-center gap-2 text-sm text-gray-600">
    <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
      <Check className="w-3 h-3 text-emerald-600" />
    </div>
    {children}
  </div>
);

// ============================================================
// WORKSPACE SELECTOR MODAL
// ============================================================

const WorkspaceSelector = ({ workspaces, selectedId, onSelect, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Select Workspace</h3>
              <p className="text-sm text-gray-500 mt-1">Choose where to install this tool</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>
        
        <div className="p-4 max-h-80 overflow-y-auto">
          <div className="space-y-2">
            {workspaces.map(ws => {
              const isSelected = selectedId === ws.id;
              const isPersonal = ws.type === 'PERSONAL';
              const isOrg = ws.type === 'ORGANISATION';
              
              return (
                <button
                  key={ws.id}
                  onClick={() => onSelect(ws.id)}
                  className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                    isSelected 
                      ? 'border-indigo-500 bg-indigo-50' 
                      : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    isPersonal ? 'bg-blue-100' : isOrg ? 'bg-purple-100' : 'bg-green-100'
                  }`}>
                    {isPersonal ? (
                      <User className={`w-5 h-5 ${isPersonal ? 'text-blue-600' : ''}`} />
                    ) : isOrg ? (
                      <Building2 className="w-5 h-5 text-purple-600" />
                    ) : (
                      <Users className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                  
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{ws.name}</span>
                      {ws.isDefault && (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">Default</span>
                      )}
                    </div>
                    <span className="text-sm text-gray-500">
                      {isPersonal ? 'Personal' : isOrg ? 'Organisation' : 'Team'}
                    </span>
                  </div>
                  
                  {isSelected && (
                    <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        
        <div className="p-4 bg-gray-50 border-t border-gray-100">
          <p className="text-xs text-gray-500 text-center">
            Tools installed to a workspace are accessible to all workspace members
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// INSTALL MODAL
// ============================================================

// Updated InstallModal with Subscription Selection
const InstallModal = ({ tool, workspaces, onInstall, onClose, installing }) => {
  const [selectedWorkspace, setSelectedWorkspace] = useState(
    workspaces.find(w => w.isDefault)?.id || workspaces[0]?.id
  );
  const [showWorkspaceSelector, setShowWorkspaceSelector] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('FREE'); // FREE, PREMIUM, MONTHLY, YEARLY, LIFETIME
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  const category = CategoryConfig[tool.category] || CategoryConfig[ToolCategory.CUSTOM];
  const IconComponent = IconMap[tool.icon] || category.icon;
  const selectedWs = workspaces.find(w => w.id === selectedWorkspace);

  // Calculate pricing based on selection
  const getPlanPrice = () => {
    if (selectedPlan === 'FREE') return 0;
    if (selectedPlan === 'PREMIUM') return tool.premiumPrice || tool.price;
    if (selectedPlan === 'MONTHLY' || selectedPlan === 'YEARLY' || selectedPlan === 'LIFETIME') return tool.price;
    return tool.price;
  };

  const handleInstall = () => {
    const installData = {
      toolId: tool.id,
      workspaceId: selectedWorkspace,
      //plan: selectedPlan,
      //price: getPlanPrice(),
      //currency: tool.currency
    };
    onInstall(tool.id, selectedWorkspace);
  };

  // Determine if install button should be enabled
  const canInstall = selectedWorkspace && (
    tool.pricing === 'FREE' || 
    selectedPlan === 'FREE' || 
    (selectedPlan && agreedToTerms)
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className={`p-6 bg-gradient-to-r ${category.gradient} flex-shrink-0`}>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
              <IconComponent className="w-8 h-8 text-white" />
            </div>
            <div className="text-white flex-1">
              <h2 className="text-xl font-bold">{tool.name}</h2>
              <p className="text-white/80 text-sm">by {tool.author}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl transition-colors">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-6">
          <div className="space-y-6">
            {/* Workspace Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Install to Workspace
              </label>
              <button
                onClick={() => setShowWorkspaceSelector(true)}
                className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-gray-300 transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    selectedWs?.type === 'PERSONAL' ? 'bg-blue-100' : 
                    selectedWs?.type === 'ORGANISATION' ? 'bg-purple-100' : 'bg-green-100'
                  }`}>
                    {selectedWs?.type === 'PERSONAL' ? (
                      <User className="w-5 h-5 text-blue-600" />
                    ) : selectedWs?.type === 'ORGANISATION' ? (
                      <Building2 className="w-5 h-5 text-purple-600" />
                    ) : (
                      <Users className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900">{selectedWs?.name || 'Select workspace'}</p>
                    <p className="text-sm text-gray-500">{selectedWs?.type || 'Choose a workspace'}</p>
                  </div>
                </div>
                <ChevronDown className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Pricing Plans Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Plan
              </label>

              {/* FREE Tool */}
              {tool.pricing === 'FREE' && (
                <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl">
                  <div className="flex items-center gap-3 mb-2">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <h3 className="text-lg font-bold text-gray-900">Free Forever</h3>
                  </div>
                  <p className="text-sm text-gray-600">Full access to all features at no cost</p>
                </div>
              )}

              {/* FREEMIUM Tool */}
              {tool.pricing === 'FREEMIUM' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Free Tier */}
                  <button
                    onClick={() => setSelectedPlan('FREE')}
                    className={`p-5 rounded-xl border-2 transition-all text-left ${
                      selectedPlan === 'FREE'
                        ? 'border-blue-600 bg-blue-50 shadow-lg'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-bold text-gray-900">Free Tier</h3>
                      {selectedPlan === 'FREE' && <CheckCircle className="w-5 h-5 text-blue-600" />}
                    </div>
                    <div className="mb-3">
                      <span className="text-2xl font-bold text-gray-900">Free</span>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-600">
                      {tool.limits?.free && Object.entries(tool.limits.free).map(([key, value]) => (
                        <li key={key} className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                          {key}: {value === -1 ? 'Unlimited' : value}
                        </li>
                      ))}
                    </ul>
                  </button>

                  {/* Premium Tier */}
                  <button
                    onClick={() => setSelectedPlan('PREMIUM')}
                    className={`p-5 rounded-xl border-2 transition-all text-left relative overflow-hidden ${
                      selectedPlan === 'PREMIUM'
                        ? 'border-purple-600 bg-purple-50 shadow-lg'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-3 py-1 rounded-bl-xl">
                      POPULAR
                    </div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-bold text-gray-900">Premium</h3>
                      {selectedPlan === 'PREMIUM' && <CheckCircle className="w-5 h-5 text-purple-600" />}
                    </div>
                    <div className="mb-3">
                      <span className="text-2xl font-bold text-gray-900">{tool.currency} {tool.premiumPrice}</span>
                    </div>
                    {tool.freemiumTrialDays > 0 && (
                      <div className="mb-3 p-2 bg-purple-100 rounded-lg">
                        <p className="text-xs font-medium text-purple-900">
                          {tool.freemiumTrialDays}-day free trial included
                        </p>
                      </div>
                    )}
                    <ul className="space-y-2 text-sm text-gray-600">
                      {tool.limits?.premium && Object.entries(tool.limits.premium).map(([key, value]) => (
                        <li key={key} className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-purple-600 flex-shrink-0" />
                          {key}: {value === -1 ? 'Unlimited' : value}
                        </li>
                      ))}
                    </ul>
                  </button>
                </div>
              )}

              {/* PAID Tool */}
              {tool.pricing === 'PAID' && (
                <div className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-900">One-Time Purchase</h3>
                    <span className="text-3xl font-bold text-indigo-600">{tool.currency} {tool.price}</span>
                  </div>
                  <div className="flex items-start gap-2 mb-4">
                    <Crown className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-600">Lifetime access to all features with one-time payment</p>
                  </div>
                  {tool.refundPeriodDays > 0 && (
                    <div className="p-3 bg-white rounded-lg border border-indigo-100">
                      <p className="text-xs text-gray-600">
                        <strong className="text-indigo-900">{tool.refundPeriodDays}-day money-back guarantee</strong> - Full refund if not satisfied
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* SUBSCRIPTION Tool */}
              {tool.pricing === 'SUBSCRIPTION' && (
                <div className="space-y-4">
                  {/* Monthly Option */}
                  {tool.subscriptionType === 'MONTHLY' && (
                    <button
                      onClick={() => setSelectedPlan('MONTHLY')}
                      className={`w-full p-5 rounded-xl border-2 transition-all text-left ${
                        selectedPlan === 'MONTHLY'
                          ? 'border-blue-600 bg-blue-50 shadow-lg'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">Monthly Subscription</h3>
                          <p className="text-sm text-gray-600">Billed every {tool.billingCycle || 1} month{tool.billingCycle > 1 ? 's' : ''}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-2xl font-bold text-gray-900">{tool.currency} {tool.price}</span>
                          <span className="text-sm text-gray-500 block">/month</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        {tool.autoRenew && <><Clock className="w-3.5 h-3.5" /> Auto-renewal enabled</>}
                        {tool.allowCancellation && <><X className="w-3.5 h-3.5 ml-2" /> Cancel anytime</>}
                      </div>
                    </button>
                  )}

                  {/* Yearly Option */}
                  {tool.subscriptionType === 'YEARLY' && (
                    <button
                      onClick={() => setSelectedPlan('YEARLY')}
                      className={`w-full p-5 rounded-xl border-2 transition-all text-left relative ${
                        selectedPlan === 'YEARLY'
                          ? 'border-green-600 bg-green-50 shadow-lg'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className="absolute top-0 right-0 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-bl-xl">
                        BEST VALUE
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">Annual Subscription</h3>
                          <p className="text-sm text-gray-600">Billed every {(tool.billingCycle || 1) * 12} months</p>
                        </div>
                        <div className="text-right">
                          <span className="text-2xl font-bold text-gray-900">{tool.currency} {tool.price}</span>
                          <span className="text-sm text-gray-500 block">/year</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        {tool.autoRenew && <><Clock className="w-3.5 h-3.5" /> Auto-renewal enabled</>}
                        {tool.allowCancellation && <><X className="w-3.5 h-3.5 ml-2" /> Cancel anytime</>}
                      </div>
                    </button>
                  )}

                  {/* Lifetime Option */}
                  {tool.subscriptionType === 'LIFETIME' && (
                    <button
                      onClick={() => setSelectedPlan('LIFETIME')}
                      className={`w-full p-5 rounded-xl border-2 transition-all text-left ${
                        selectedPlan === 'LIFETIME'
                          ? 'border-amber-600 bg-amber-50 shadow-lg'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            <Crown className="w-5 h-5 text-amber-600" />
                            Lifetime Access
                          </h3>
                          <p className="text-sm text-gray-600">One-time payment, forever yours</p>
                        </div>
                        <div className="text-right">
                          <span className="text-2xl font-bold text-gray-900">{tool.currency} {tool.price}</span>
                          <span className="text-sm text-gray-500 block">once</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">No recurring charges. Full access for life.</p>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Permissions */}
            {tool.permissions && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Permissions Required
                </label>
                <div className="flex flex-wrap gap-2">
                  {tool.permissions.map(perm => (
                    <span key={perm} className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg">
                      <Shield className="w-3.5 h-3.5" />
                      {perm.charAt(0).toUpperCase() + perm.slice(1)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Terms Agreement for Paid Plans */}
            {tool.pricing !== 'FREE' && selectedPlan !== 'FREE' && (
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-600">
                    I agree to the terms of service and understand that {
                      tool.pricing === 'SUBSCRIPTION' && selectedPlan !== 'LIFETIME'
                        ? `my subscription will ${tool.autoRenew ? 'automatically renew' : 'need manual renewal'} every billing cycle`
                        : `this is a ${tool.pricing === 'PAID' ? 'one-time' : ''} payment`
                    }. {tool.pricing === 'FREEMIUM' && tool.freemiumTrialDays > 0 && selectedPlan === 'PREMIUM' && 
                      `I will be charged after the ${tool.freemiumTrialDays}-day free trial period.`}
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-xl transition-colors"
          >
            Cancel
          </button>
          <div className="flex items-center gap-3">
            {selectedPlan && selectedPlan !== 'FREE' && (
              <div className="text-right mr-2">
                <p className="text-xs text-gray-500">Total</p>
                <p className="text-lg font-bold text-gray-900">{tool.currency} {getPlanPrice()}</p>
              </div>
            )}
            <button
              onClick={handleInstall}
              disabled={installing || !canInstall}
              className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-xl hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg shadow-indigo-500/25"
            >
              {installing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Installing...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  {selectedPlan === 'FREE' || tool.pricing === 'FREE' ? 'Install Free' : `Install for ${tool.currency} ${getPlanPrice()}`}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Workspace Selector */}
      {showWorkspaceSelector && (
        <WorkspaceSelector
          workspaces={workspaces}
          selectedId={selectedWorkspace}
          onSelect={(id) => { setSelectedWorkspace(id); setShowWorkspaceSelector(false); }}
          onClose={() => setShowWorkspaceSelector(false)}
        />
      )}
    </div>
  );
};

// ============================================================
// EDIT TOOL MODAL (for owners)
// ============================================================

// Updated EditToolModal with Monetization Config
const EditToolModal = ({ tool, onSave, onClose, saving }) => {
  const [formData, setFormData] = useState({
    customName: tool.customName || tool.name,
    customDescription: tool.customDescription || tool.description,
    isVisible: tool.isVisible !== false,
    isPinned: tool.isPinned || false,
    notes: tool.notes || '',
    // Monetization fields
    pricing: tool.pricing || 'FREE',
    price: tool.price || 0,
    premiumPrice: tool.premiumPrice || 0,
    currency: tool.currency || 'ZAR',
    freemiumTrialDays: tool.freemiumTrialDays || 14,
    subscriptionType: tool.subscriptionType || 'MONTHLY',
    billingCycle: tool.billingCycle || 1,
    allowCancellation: tool.allowCancellation !== false,
    refundPeriodDays: tool.refundPeriodDays || 7,
    autoRenew: tool.autoRenew !== false
  });

  const category = CategoryConfig[tool.category] || CategoryConfig[ToolCategory.CUSTOM];

  const handleSave = () => {
    onSave(tool.id, formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${category.gradient} flex items-center justify-center`}>
                <Edit3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Edit Tool Configuration</h3>
                <p className="text-sm text-gray-500">Customize display and monetization settings</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-6">
          <div className="space-y-6">
            {/* Display Settings Section */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Settings2 className="w-4 h-4" />
                Display Settings
              </h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Display Name</label>
                  <input
                    type="text"
                    value={formData.customName}
                    onChange={(e) => setFormData(prev => ({ ...prev, customName: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Custom display name..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Original: {tool.name}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={formData.customDescription}
                    onChange={(e) => setFormData(prev => ({ ...prev, customDescription: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    rows={3}
                    placeholder="Custom description..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes (private)</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    rows={2}
                    placeholder="Add private notes about this tool..."
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    {formData.isVisible ? <Eye className="w-5 h-5 text-gray-600" /> : <Lock className="w-5 h-5 text-gray-400" />}
                    <div>
                      <p className="font-medium text-gray-900">Visible to Members</p>
                      <p className="text-sm text-gray-500">Show this tool to workspace members</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, isVisible: !prev.isVisible }))}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      formData.isVisible ? 'bg-indigo-600' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      formData.isVisible ? 'left-7' : 'left-1'
                    }`} />
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Star className={`w-5 h-5 ${formData.isPinned ? 'text-amber-500 fill-amber-500' : 'text-gray-400'}`} />
                    <div>
                      <p className="font-medium text-gray-900">Pin to Top</p>
                      <p className="text-sm text-gray-500">Show this tool first in the list</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, isPinned: !prev.isPinned }))}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      formData.isPinned ? 'bg-amber-500' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      formData.isPinned ? 'left-7' : 'left-1'
                    }`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Monetization Settings Section */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Receipt className="w-4 h-4" />
                Monetization Settings
              </h4>
              
              <div className="space-y-4">
                {/* Pricing Model */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pricing Model</label>
                  <div className="grid grid-cols-2 gap-3">
                    {['FREE', 'FREEMIUM', 'PAID', 'SUBSCRIPTION'].map(type => (
                      <button
                        key={type}
                        onClick={() => setFormData(prev => ({ ...prev, pricing: type }))}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          formData.pricing === type
                            ? 'border-indigo-600 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-sm font-semibold text-gray-900">{type}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {type === 'FREE' && 'No cost'}
                          {type === 'FREEMIUM' && 'Free + Premium'}
                          {type === 'PAID' && 'One-time payment'}
                          {type === 'SUBSCRIPTION' && 'Recurring billing'}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Currency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="ZAR">ZAR (South African Rand)</option>
                    <option value="USD">USD (US Dollar)</option>
                    <option value="EUR">EUR (Euro)</option>
                    <option value="GBP">GBP (British Pound)</option>
                  </select>
                </div>

                {/* Free Pricing - No additional fields */}
                {formData.pricing === 'FREE' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-green-900">Free Forever</p>
                        <p className="text-xs text-green-700 mt-1">No payment required. Available to all users.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Freemium Pricing */}
                {formData.pricing === 'FREEMIUM' && (
                  <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Premium Price</label>
                        <input
                          type="number"
                          value={formData.premiumPrice}
                          onChange={(e) => setFormData(prev => ({ ...prev, premiumPrice: parseFloat(e.target.value) || 0 }))}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Trial Period (days)</label>
                        <input
                          type="number"
                          value={formData.freemiumTrialDays}
                          onChange={(e) => setFormData(prev => ({ ...prev, freemiumTrialDays: parseInt(e.target.value) || 0 }))}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="14"
                          min="0"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-blue-700">
                      Users get {formData.freemiumTrialDays} days free trial of premium features
                    </p>
                  </div>
                )}

                {/* Paid Pricing */}
                {formData.pricing === 'PAID' && (
                  <div className="space-y-4 p-4 bg-purple-50 border border-purple-200 rounded-xl">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">One-time Price</label>
                        <input
                          type="number"
                          value={formData.price}
                          onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Refund Period (days)</label>
                        <input
                          type="number"
                          value={formData.refundPeriodDays}
                          onChange={(e) => setFormData(prev => ({ ...prev, refundPeriodDays: parseInt(e.target.value) || 0 }))}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="7"
                          min="0"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-purple-700">
                      Lifetime access after one-time payment. {formData.refundPeriodDays}-day money-back guarantee.
                    </p>
                  </div>
                )}

                {/* Subscription Pricing */}
                {formData.pricing === 'SUBSCRIPTION' && (
                  <div className="space-y-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Subscription Type</label>
                      <div className="grid grid-cols-3 gap-3">
                        {['MONTHLY', 'YEARLY', 'LIFETIME'].map(type => (
                          <button
                            key={type}
                            onClick={() => setFormData(prev => ({ ...prev, subscriptionType: type }))}
                            className={`p-3 rounded-xl border-2 transition-all ${
                              formData.subscriptionType === type
                                ? 'border-amber-600 bg-amber-100'
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                          >
                            <div className="text-sm font-semibold text-gray-900">{type}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {formData.subscriptionType === 'LIFETIME' ? 'Lifetime Price' : 'Subscription Price'}
                        </label>
                        <input
                          type="number"
                          value={formData.price}
                          onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      {formData.subscriptionType !== 'LIFETIME' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Billing Cycle (months)</label>
                          <input
                            type="number"
                            value={formData.billingCycle}
                            onChange={(e) => setFormData(prev => ({ ...prev, billingCycle: parseInt(e.target.value) || 1 }))}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="1"
                            min="1"
                          />
                        </div>
                      )}
                    </div>

                    {formData.subscriptionType !== 'LIFETIME' && (
                      <>
                        <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-200">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Auto-Renewal</p>
                            <p className="text-xs text-gray-500">Automatically renew subscription</p>
                          </div>
                          <button
                            onClick={() => setFormData(prev => ({ ...prev, autoRenew: !prev.autoRenew }))}
                            className={`relative w-12 h-6 rounded-full transition-colors ${
                              formData.autoRenew ? 'bg-amber-600' : 'bg-gray-300'
                            }`}
                          >
                            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                              formData.autoRenew ? 'left-7' : 'left-1'
                            }`} />
                          </button>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-200">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Allow Cancellation</p>
                            <p className="text-xs text-gray-500">Users can cancel anytime</p>
                          </div>
                          <button
                            onClick={() => setFormData(prev => ({ ...prev, allowCancellation: !prev.allowCancellation }))}
                            className={`relative w-12 h-6 rounded-full transition-colors ${
                              formData.allowCancellation ? 'bg-amber-600' : 'bg-gray-300'
                            }`}
                          >
                            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                              formData.allowCancellation ? 'left-7' : 'left-1'
                            }`} />
                          </button>
                        </div>
                      </>
                    )}

                    <p className="text-xs text-amber-700">
                      {formData.subscriptionType === 'MONTHLY' && `Billed every ${formData.billingCycle} month${formData.billingCycle > 1 ? 's' : ''}`}
                      {formData.subscriptionType === 'YEARLY' && `Billed every ${formData.billingCycle * 12} month${formData.billingCycle > 1 ? 's' : ''}`}
                      {formData.subscriptionType === 'LIFETIME' && 'One-time payment for lifetime access'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// TOOL CARD
// ============================================================

const ToolCard = ({ tool, isInstalled, isOwner, onInstall, onViewDetails, onEdit }) => {
  const category = CategoryConfig[tool.category] || CategoryConfig[ToolCategory.CUSTOM];
  const IconComponent = IconMap[tool.icon] || category.icon;

  return (
    <div className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-gray-200 hover:shadow-xl transition-all duration-300">
      {/* Header with gradient */}
      <div className={`h-36 bg-gradient-to-br ${category.gradient} flex items-center justify-center relative overflow-hidden`}>
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
        </div>
        
        <div className="relative">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
            <IconComponent className="w-8 h-8 text-white" />
          </div>
        </div>
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          {tool.featured && (
            <span className="flex items-center gap-1 px-2 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-full">
              <Sparkles className="w-3 h-3" /> Featured
            </span>
          )}
        </div>
        
        <div className="absolute top-3 right-3">
          <PricingBadge pricing={tool.pricing} price={tool.price} currency={tool.currency} />
        </div>

        {isInstalled && (
          <div className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-1 bg-emerald-500 text-white text-xs font-medium rounded-full shadow-lg">
            <BadgeCheck className="w-3 h-3" /> Installed
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-2">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">
            {tool.customName || tool.name}
          </h3>
          {isOwner && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(tool); }}
              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              <Edit3 className="w-4 h-4" />
            </button>
          )}
        </div>
        
        <p className="text-sm text-gray-500 line-clamp-2 mb-4">
          {tool.customDescription || tool.description}
        </p>
        
        {/* Meta info */}
        <div className="flex items-center gap-4 mb-4 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <Layers className="w-3.5 h-3.5" />
            v{tool.version}
          </span>
          <span className="flex items-center gap-1">
            <User className="w-3.5 h-3.5" />
            {tool.author}
          </span>
        </div>
        
        {/* Tags */}
        {tool.tags && tool.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {tool.tags.slice(0, 3).map(tag => (
              <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-md">
                {tag}
              </span>
            ))}
            {tool.tags.length > 3 && (
              <span className="px-2 py-0.5 text-gray-400 text-xs">
                +{tool.tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => onViewDetails(tool)}
            className="flex-1 py-2.5 px-4 text-gray-700 hover:bg-gray-100 font-medium text-sm rounded-xl transition-colors"
          >
            Details
          </button>
          {isInstalled ? (
            <button
              disabled
              className="flex-1 py-2.5 px-4 bg-gray-100 text-gray-500 font-medium text-sm rounded-xl flex items-center justify-center gap-2"
            >
              <Check className="w-4 h-4" /> Installed
            </button>
          ) : (
            <button
              onClick={() => onInstall(tool)}
              className={`flex-1 py-2.5 px-4 bg-gradient-to-r ${category.gradient} text-white font-medium text-sm rounded-xl flex items-center justify-center gap-2 hover:shadow-lg transition-all`}
            >
              <Download className="w-4 h-4" /> Install
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// TOOL DETAIL MODAL
// ============================================================

const ToolDetailModal = ({ tool, isInstalled, isOwner, onInstall, onEdit, onClose }) => {
  if (!tool) return null;

  const category = CategoryConfig[tool.category] || CategoryConfig[ToolCategory.CUSTOM];
  const IconComponent = IconMap[tool.icon] || category.icon;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className={`h-48 bg-gradient-to-br ${category.gradient} relative overflow-hidden`}>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
          </div>
          
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          
          <div className="absolute bottom-6 left-6 flex items-end gap-4">
            <div className="w-20 h-20 bg-white rounded-2xl shadow-xl flex items-center justify-center">
              <IconComponent className={`w-10 h-10 text-${category.color}-500`} />
            </div>
            <div className="pb-1">
              <h2 className="text-2xl font-bold text-white">{tool.name}</h2>
              <p className="text-white/80">by {tool.author}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-12rem-5rem)]">
          {/* Meta badges */}
          <div className="flex flex-wrap gap-2 mb-6">
            <PricingBadge pricing={tool.pricing} price={tool.price} currency={tool.currency} size="lg" />
            <span className={`px-3 py-1.5 bg-${category.color}-50 text-${category.color}-700 text-sm font-medium rounded-full`}>
              {category.label}
            </span>
            <span className="px-3 py-1.5 bg-gray-100 text-gray-600 text-sm font-medium rounded-full">
              v{tool.version}
            </span>
            {tool.featured && (
              <span className="px-3 py-1.5 bg-amber-100 text-amber-700 text-sm font-medium rounded-full flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5" /> Featured
              </span>
            )}
          </div>

          {/* Description */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">About this tool</h3>
            <p className="text-gray-600 leading-relaxed">{tool.longDescription || tool.description}</p>
          </div>

          {/* Features */}
          {tool.dataCollections && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Features included</h3>
              <div className="grid grid-cols-2 gap-3">
                {tool.dataCollections.map(collection => (
                  <FeatureCheckItem key={collection}>
                    {collection.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </FeatureCheckItem>
                ))}
              </div>
            </div>
          )}

          {/* Permissions */}
          {tool.permissions && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Permissions</h3>
              <div className="flex flex-wrap gap-2">
                {tool.permissions.map(perm => (
                  <span key={perm} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-700 text-sm rounded-lg">
                    <Shield className="w-3.5 h-3.5" />
                    {perm.charAt(0).toUpperCase() + perm.slice(1)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {tool.tags && tool.tags.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {tool.tags.map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 text-sm rounded-lg">
                    <Tag className="w-3.5 h-3.5" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Pricing tiers for freemium */}
          {tool.pricing === ToolPricing.FREEMIUM && tool.limits && (
            <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
              <h3 className="font-semibold text-gray-900 mb-4">Pricing Tiers</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-white rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Unlock className="w-4 h-4 text-gray-600" />
                    </div>
                    <span className="font-medium text-gray-900">Free</span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {Object.entries(tool.limits.free || {}).map(([key, value]) => (
                      <li key={key} className="flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-gray-400" />
                        {key.replace(/([A-Z])/g, ' $1')}: {value}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-4 bg-white rounded-xl border-2 border-indigo-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Crown className="w-4 h-4 text-indigo-600" />
                    </div>
                    <span className="font-medium text-gray-900">Premium</span>
                    <span className="text-xs text-indigo-600 font-medium">{tool.currency} {tool.premiumPrice}</span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {Object.entries(tool.limits.premium || {}).map(([key, value]) => (
                      <li key={key} className="flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-indigo-500" />
                        {key.replace(/([A-Z])/g, ' $1')}: {value === -1 ? 'Unlimited' : value}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {tool.pricing === ToolPricing.PAID && (
              <span className="font-semibold text-gray-900 text-lg">{tool.currency} {tool.price}</span>
            )}
            {tool.pricing === ToolPricing.FREE && 'Free forever'}
            {tool.pricing === ToolPricing.FREEMIUM && 'Start free, upgrade anytime'}
          </div>
          
          <div className="flex items-center gap-3">
            {isInstalled && isOwner && (
              <button
                onClick={() => onEdit(tool)}
                className="px-4 py-2.5 text-gray-700 hover:bg-gray-200 rounded-xl flex items-center gap-2 transition-colors"
              >
                <Edit3 className="w-4 h-4" /> Customize
              </button>
            )}
            
            {isInstalled ? (
              <button
                disabled
                className="px-6 py-2.5 bg-gray-100 text-gray-500 font-medium rounded-xl flex items-center gap-2"
              >
                <BadgeCheck className="w-5 h-5" /> Already Installed
              </button>
            ) : (
              <button
                onClick={() => onInstall(tool)}
                className={`px-6 py-2.5 bg-gradient-to-r ${category.gradient} text-white font-medium rounded-xl flex items-center gap-2 hover:shadow-lg shadow-${category.color}-500/25 transition-all`}
              >
                <Download className="w-5 h-5" />
                {tool.pricing === ToolPricing.PAID ? `Install for ${tool.currency} ${tool.price}` : 'Install Free'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MAIN MARKETPLACE COMPONENT
// ============================================================

const ToolsMarketplace = ({ 
  bookId, 
  installedTools = [],
  workspaces = [],
  isOwner = false,
  onInstall, 
  onUpdateTool,
  onBack, 
  customStyling = {} 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPricing, setSelectedPricing] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedTool, setSelectedTool] = useState(null);
  const [toolToInstall, setToolToInstall] = useState(null);
  const [toolToEdit, setToolToEdit] = useState(null);
  const [installing, setInstalling] = useState(false);
  const [saving, setSaving] = useState(false);

  // Get installed tool IDs (use toolId field, not auto-generated doc id)
  const installedToolIds = useMemo(() => installedTools.map(t => t.toolId || t.id), [installedTools]);

  // Get all tools from catalog with installed customizations merged
  const allTools = useMemo(() => {
    const catalogTools = toolsService.getMarketplaceCatalog({
      category: selectedCategory,
      pricing: selectedPricing,
      search: searchQuery
    });

    // Merge with installed customizations (match by toolId)
    return catalogTools.map(tool => {
      const installed = installedTools.find(t => (t.toolId || t.id) === tool.id);
      if (installed) {
        return { ...tool, ...installed, installationId: installed.id };
      }
      return tool;
    });
  }, [selectedCategory, selectedPricing, searchQuery, installedTools]);

  // Get unique categories with counts
  const categoriesWithCounts = useMemo(() => {
    const counts = {};
    Object.values(TOOL_CATALOG).forEach(t => {
      counts[t.category] = (counts[t.category] || 0) + 1;
    });
    return counts;
  }, []);

  // Featured tools
  const featuredTools = useMemo(() => {
    return allTools.filter(t => t.featured).slice(0, 4);
  }, [allTools]);

  // Default workspaces if none provided
  const availableWorkspaces = workspaces.length > 0 ? workspaces : [
    { id: 'personal', name: 'Personal Workspace', type: 'PERSONAL', isDefault: true }
  ];

  // Handle install
  const handleInstall = async (toolId, workspaceId) => {
    setInstalling(true);
    try {
      await onInstall(toolId, workspaceId);
      setToolToInstall(null);
      setSelectedTool(null);
    } catch (error) {
      console.error('Error installing tool:', error);
      alert('Failed to install tool: ' + error.message);
    } finally {
      setInstalling(false);
    }
  };

  // Handle edit save
  const handleSaveEdit = async (toolId, updates) => {
    setSaving(true);
    try {
      await onUpdateTool(toolId, updates);
      setToolToEdit(null);
    } catch (error) {
      console.error('Error updating tool:', error);
      alert('Failed to update tool: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="max-w-7xl mx-auto p-3 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-white rounded-xl shadow-sm border border-gray-200 transition-colors">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Tool Marketplace</h1>
              <p className="text-gray-500">Discover tools to enhance your productivity</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 bg-white rounded-xl p-1 shadow-sm border border-gray-200">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search for tools..."
            className="w-full pl-14 pr-5 py-4 bg-white border border-gray-200 rounded-2xl shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-lg"
          />
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap items-center gap-3">
          <CategoryPill
            category={null}
            selected={!selectedCategory}
            onClick={() => setSelectedCategory(null)}
            count={Object.keys(TOOL_CATALOG).length}
          />
          {Object.keys(categoriesWithCounts).map(cat => (
            <CategoryPill
              key={cat}
              category={cat}
              selected={selectedCategory === cat}
              onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
              count={categoriesWithCounts[cat]}
            />
          ))}
        </div>

        {/* Pricing Filter */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">Filter by:</span>
          {[
            { value: null, label: 'All' },
            { value: ToolPricing.FREE, label: 'Free', color: 'emerald' },
            { value: ToolPricing.FREEMIUM, label: 'Freemium', color: 'blue' },
            { value: ToolPricing.PAID, label: 'Paid', color: 'amber' }
          ].map(option => (
            <button
              key={option.label}
              onClick={() => setSelectedPricing(option.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                selectedPricing === option.value
                  ? option.color ? `bg-${option.color}-100 text-${option.color}-700` : 'bg-gray-900 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Featured Section */}
        {!searchQuery && !selectedCategory && !selectedPricing && featuredTools.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-5">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <h2 className="text-xl font-bold text-gray-900">Featured Tools</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {featuredTools.map(tool => (
                <ToolCard
                  key={tool.id}
                  tool={tool}
                  isInstalled={installedToolIds.includes(tool.id)}
                  isOwner={isOwner}
                  onInstall={(tool) => setToolToInstall(tool)}
                  onViewDetails={setSelectedTool}
                  onEdit={setToolToEdit}
                />
              ))}
            </div>
          </div>
        )}

        {/* All Tools */}
        <div>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-bold text-gray-900">
              {searchQuery ? `Search Results` : selectedCategory ? `${CategoryConfig[selectedCategory]?.label} Tools` : 'All Tools'}
              <span className="ml-2 text-gray-400 font-normal">({allTools.length})</span>
            </h2>
          </div>
          
          {allTools.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No tools found</h3>
              <p className="text-gray-500 mb-6">Try adjusting your search or filters</p>
              <button
                onClick={() => { setSearchQuery(''); setSelectedCategory(null); setSelectedPricing(null); }}
                className="px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-5'
              : 'space-y-4'
            }>
              {allTools.map(tool => (
                <ToolCard
                  key={tool.id}
                  tool={tool}
                  isInstalled={installedToolIds.includes(tool.id)}
                  isOwner={isOwner}
                  onInstall={(tool) => setToolToInstall(tool)}
                  onViewDetails={setSelectedTool}
                  onEdit={setToolToEdit}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tool Detail Modal */}
      {selectedTool && (
        <ToolDetailModal
          tool={selectedTool}
          isInstalled={installedToolIds.includes(selectedTool.id)}
          isOwner={isOwner}
          onInstall={(tool) => { setSelectedTool(null); setToolToInstall(tool); }}
          onEdit={(tool) => { setSelectedTool(null); setToolToEdit(tool); }}
          onClose={() => setSelectedTool(null)}
        />
      )}

      {/* Install Modal */}
      {toolToInstall && (
        <InstallModal
          tool={toolToInstall}
          workspaces={availableWorkspaces}
          onInstall={handleInstall}
          onClose={() => setToolToInstall(null)}
          installing={installing}
        />
      )}

      {/* Edit Modal */}
      {toolToEdit && (
        <EditToolModal
          tool={toolToEdit}
          onSave={handleSaveEdit}
          onClose={() => setToolToEdit(null)}
          saving={saving}
        />
      )}
    </div>
  );
};

export default ToolsMarketplace;